
const STORAGE_KEY = 'kienzle:wishlist';
const CHANGE_EVENT = 'wishlist:change';

let memoryFallback = [];
let storageAvailable = true;

function readStorage() {
  if (!storageAvailable) return [...memoryFallback];

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];

    return Array.isArray(parsed) ? parsed.filter((handle) => typeof handle === 'string') : [];
  } catch (error) {
    storageAvailable = false;
    return [...memoryFallback];
  }
}

function writeStorage(handles) {
  memoryFallback = [...handles];

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  } catch (error) {
    storageAvailable = false;
  }

  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handles } }));
}

const WishlistStore = {
  items() {
    return readStorage();
  },

  count() {
    return readStorage().length;
  },

  has(handle) {
    return readStorage().includes(handle);
  },

  add(handle) {
    const handles = readStorage().filter((entry) => entry !== handle);
    handles.unshift(handle);
    writeStorage(handles);
  },

  remove(handle) {
    writeStorage(readStorage().filter((entry) => entry !== handle));
  },

  toggle(handle) {
    const saved = this.has(handle);
    saved ? this.remove(handle) : this.add(handle);

    return !saved;
  },
};

window.addEventListener('storage', (event) => {
  if (event.key !== STORAGE_KEY) return;

  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handles: readStorage() } }));
});

window.KienzleWishlist = WishlistStore;

class WishlistToggle extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();

    const { signal } = this.#abortController;

    this.button?.addEventListener('click', this.#onClick.bind(this), { signal });
    document.addEventListener(CHANGE_EVENT, this.#render.bind(this), { signal });

    this.#render();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  get button() {
    return this.querySelector('button');
  }

  get handle() {
    return this.getAttribute('handle');
  }

  #onClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.handle) return;

    WishlistStore.toggle(this.handle);
  }

  #render() {
    const button = this.button;

    if (!button || !this.handle) return;

    const saved = WishlistStore.has(this.handle);
    const label = saved ? button.dataset.labelRemove : button.dataset.labelAdd;

    button.classList.toggle('is-active', saved);
    button.setAttribute('aria-pressed', String(saved));

    if (label) button.setAttribute('aria-label', label);
  }
}

if (!window.customElements.get('wishlist-toggle')) {
  window.customElements.define('wishlist-toggle', WishlistToggle);
}

class WishlistCount extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();

    document.addEventListener(CHANGE_EVENT, this.#render.bind(this), {
      signal: this.#abortController.signal,
    });

    this.#render();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #render() {
    const count = WishlistStore.count();

    this.textContent = count > 0 ? String(count) : '';
    this.classList.toggle('is-visible', count > 0);
  }
}

if (!window.customElements.get('wishlist-count')) {
  window.customElements.define('wishlist-count', WishlistCount);
}

class WishlistList extends HTMLElement {
  #abortController;
  #renderedHandles = '';

  connectedCallback() {
    this.#abortController = new AbortController();

    document.addEventListener(CHANGE_EVENT, this.#onChange.bind(this), {
      signal: this.#abortController.signal,
    });

    this.#render();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  get grid() {
    return this.querySelector('[data-wishlist-items]');
  }

  get emptyState() {
    return this.querySelector('[data-wishlist-empty]');
  }

  #show(state) {
    if (this.grid) this.grid.hidden = state !== 'items';
    if (this.emptyState) this.emptyState.hidden = state !== 'empty';

    this.dataset.state = state;
  }

  #onChange() {
    if (WishlistStore.items().join(',') === this.#renderedHandles) return;

    this.#render();
  }

  #cardUrl(handle) {
    return this.dataset.cardUrl.replace('__handle__', encodeURIComponent(handle));
  }

  async #render() {
    const handles = WishlistStore.items();

    this.#renderedHandles = handles.join(',');

    if (handles.length === 0) {
      if (this.grid) this.grid.innerHTML = '';
      this.#show('empty');
      return;
    }

    this.#show('items');

    const cards = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(this.#cardUrl(handle));

          if (!response.ok) {
            WishlistStore.remove(handle);
            return null;
          }

          return await response.text();
        } catch (error) {
          return null;
        }
      })
    );

    if (this.#renderedHandles !== handles.join(',')) return;

    const markup = cards.filter(Boolean).join('');

    if (this.grid) this.grid.innerHTML = markup;
    this.#show(markup === '' ? 'empty' : 'items');
  }
}

if (!window.customElements.get('wishlist-list')) {
  window.customElements.define('wishlist-list', WishlistList);
}
